import { c as _c } from "react/compiler-runtime";
import { readFragment } from "@opensea/graphql";
import { useHandoff } from "@opensea/navigation";
import { Button, Image } from "@opensea/next-ui-kit";
import { Container, classNames, Flex, GradientMask, isTypingInInputField, useCarousel } from "@opensea/ui-kit";
import { useEventListener } from "@opensea/ui-kit/hooks";
import { ArrowLeftAlt, ArrowRightAlt } from "@opensea/ui-kit/icons";
import { useEffect, useRef, useState } from "react";
import { useSwipeable } from "react-swipeable";
import { ItemViewModalFragment } from "./ItemViewModal.fragment";
export function ThumbnailPicker(props) {
  const $ = _c(9);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  const items = useHandoff();
  let t0;
  if ($[3] !== items || $[4] !== rest) {
    t0 = Array.isArray(items) ? <ThumbnailPickerWithItems {...rest} items={items.flatMap(_temp)} /> : null;
    $[3] = items;
    $[4] = rest;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== className || $[7] !== t0) {
    t1 = <Flex className={className}>{t0}</Flex>;
    $[6] = className;
    $[7] = t0;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
function _temp(item) {
  return item ? [item] : [];
}
function ThumbnailPickerWithItems({
  initialTokenId,
  items: itemKeys,
  onChange
}) {
  const items = itemKeys.map(itemKey => readFragment(ItemViewModalFragment, itemKey));
  const [selectedIndex, setSelectedIndex] = useState(items.findIndex(item => item.tokenId === initialTokenId));
  const [emblaThumbsRef, emblaThumbsApi] = useCarousel({
    containScroll: "keepSnaps",
    dragFree: true
  });
  useEffect(() => {
    emblaThumbsApi?.scrollTo(selectedIndex);
  }, [emblaThumbsApi, selectedIndex]);
  function handleSelect(item_0, index) {
    setSelectedIndex(index);
    onChange(item_0);
  }
  function handlePrev() {
    const index_0 = Math.max(0, selectedIndex - 1);
    const item_1 = items[index_0];
    setSelectedIndex(index_0);
    onChange(item_1);
  }
  function handleNext() {
    const index_1 = Math.min(items.length - 1, selectedIndex + 1);
    const item_2 = items[index_1];
    setSelectedIndex(index_1);
    emblaThumbsApi?.scrollTo(index_1);
    onChange(item_2);
  }
  useEventListener("keydown", event => {
    // Don't execute hotkeys if the user is currently typing in an input field
    if (isTypingInInputField()) {
      return;
    }
    if (event.key === "ArrowLeft" || event.key === "j") {
      handlePrev();
    } else if (event.key === "ArrowRight" || event.key === "k") {
      handleNext();
    }
  });
  const scrollableElementsCache = useRef(new WeakSet());
  function checkForHorizontalScroll(element) {
    if (scrollableElementsCache.current.has(element)) {
      return true;
    }
    const computedStyle = window.getComputedStyle(element);
    const hasHorizontalScroll = computedStyle.overflowX === "auto" || computedStyle.overflowX === "scroll" || element.scrollWidth > element.clientWidth;
    if (hasHorizontalScroll) {
      scrollableElementsCache.current.add(element);
      return true;
    }
    const parent = element.parentElement;
    if (parent && parent !== document.body) {
      return checkForHorizontalScroll(parent);
    }
    return false;
  }
  const {
    ref: documentRef
  } = useSwipeable({
    onSwipedLeft: eventData => {
      const target = eventData.event.target;
      if (target instanceof Element && !checkForHorizontalScroll(target)) {
        handleNext();
      }
    },
    onSwipedRight: eventData_0 => {
      const target_0 = eventData_0.event.target;
      if (target_0 instanceof Element && !checkForHorizontalScroll(target_0)) {
        handlePrev();
      }
    },
    delta: 150
  });
  useEffect(() => {
    documentRef(document);
    return () => {
      // @ts-expect-error - This is intentional to clean up refs
      documentRef({});
    };
  }, [documentRef]);
  return <Flex className="w-full items-center justify-center gap-7">
      <div className="hidden grow-0 md:flex">
        <Button aria-label="Prev" className="focus:routline-hidden" disabled={selectedIndex === 0} icon={ArrowLeftAlt} onClick={handlePrev} variant="secondary" />
      </div>

      <GradientMask className="flex size-full items-center overflow-x-hidden md:max-w-[60vw] lg:max-w-[600px]">
        <Container className="py-0.5">
          <div className="touch-pan-y" ref={emblaThumbsRef}>
            <div className="flex">
              {items.map((item_3, index_2) => <Thumb image={item_3.imageUrl || ""} key={item_3.id} onClick={() => {
              handleSelect(item_3, index_2);
            }} selected={index_2 === selectedIndex} />)}
            </div>
          </div>
        </Container>
      </GradientMask>

      <div className="hidden grow-0 md:flex">
        <Button aria-label="Next" className={classNames("focus:outline-hidden")} disabled={selectedIndex === items.length - 1} icon={ArrowRightAlt} iconSide="right" onClick={handleNext} variant="secondary" />
      </div>
    </Flex>;
}
export function Thumb(t0) {
  const $ = _c(8);
  const {
    selected,
    image,
    onClick
  } = t0;
  const t1 = selected ? "shadow-[0_0_0_2px_white]" : "border-transparent opacity-60";
  let t2;
  if ($[0] !== t1) {
    t2 = classNames("mr-1 flex size-10 shrink-0 grow-0 items-center justify-center overflow-hidden rounded bg-white sm:mr-2", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== image) {
    t3 = <Image className="object-cover" frameTime={1} height={48} src={image} width={48} />;
    $[2] = image;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onClick || $[5] !== t2 || $[6] !== t3) {
    t4 = <button className={t2} onClick={onClick} type="button">{t3}</button>;
    $[4] = onClick;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}